import Image from 'next/image'
import { Container } from 'react-grid-system'
import { Button, Eyebrow, Heading, Paragraph } from '~elements'
import Video from 'components/elements/Video/Video'
import { AssetType, ButtonProps, ResponsiveMultimedia, RichTextType } from '~types'
import cx from 'classnames'
import styles from './HeroEdpHeader.module.scss'
import { NEXT_IMAGE_QUALITY } from '~constants'
import { SizeBreakpointLg, SizeBreakpointSm } from '~styles/dist/tokens.mjs'
import { useWindowSize } from 'utils/hooks'

interface HeroEdpHeaderProps {
  buttonGroup: Array<ButtonProps>
  ctaSection?: Array<ctaSectionProps>
  description: RichTextType
  readMoreField: RichTextType
  subHeadline: string
  title: string
  responsiveMultimedia: ResponsiveMultimedia
  stackButtonsOnMobile?: boolean
}
interface ctaSectionProps {
  buttonText: string
  component: string
  id: string
  urlBase: string
}

function isImagePropsWithUrl(asset: unknown): asset is AssetType {
  return (
    typeof asset === 'object' &&
    asset !== null &&
    'url' in asset &&
    typeof (asset as { url?: unknown }).url === 'string'
  )
}

const HeroEdpHeader = ({
  buttonGroup,
  description,
  readMoreField,
  subHeadline = '',
  title,
  responsiveMultimedia,
  stackButtonsOnMobile
}: HeroEdpHeaderProps) => {
  const {
    posterImageSm,
    posterImageXl,
    backgroundVideoSm,
    backgroundVideoXl,
    allowPause,
    allowVolume,
    vimeoIdXl,
    vimeoIdSm
  } = responsiveMultimedia ?? {}
  const { isBreakpoint: isBelowSm } = useWindowSize('sm', '<')
  const linkModule = 'hero_edp'
  const fallbackImage = posterImageSm ?? posterImageXl
  const fallbackAlt = posterImageSm?.altText ?? posterImageXl?.altText ?? title ?? ''

  const hasVideo = Boolean(backgroundVideoXl ?? backgroundVideoSm ?? vimeoIdXl ?? vimeoIdSm)

  const backgroundImageHasURL = isImagePropsWithUrl(fallbackImage)

  const handleButtonAnchor = (elementID: string) => {
    const targetElement = document.getElementById(elementID)
    if (targetElement != null) {
      targetElement.scrollIntoView({
        behavior: 'smooth'
      })
    }
  }

  return (
    <div className={styles['hero']}>
      <div
        className={cx(
          styles['hero__media-wrapper'],
          backgroundImageHasURL && styles['hero__media-wrapper--multimedia']
        )}
      >
        {hasVideo ? (
          <Video
            videoLoop={responsiveMultimedia?.videoLoop ?? false}
            backgroundVideoXl={backgroundVideoXl}
            backgroundVideoSm={backgroundVideoSm}
            posterImageXl={posterImageXl}
            posterImageSm={posterImageSm}
            allowPause={allowPause}
            allowVolume={allowVolume}
            vimeoIdXl={vimeoIdXl}
            vimeoIdSm={vimeoIdSm}
          />
        ) : responsiveMultimedia ? (
          <picture>
            {posterImageXl?.url ? (
              <source srcSet={posterImageXl.url} media={`(min-width: ${SizeBreakpointLg}px)`} />
            ) : null}
            {posterImageSm?.url ? (
              <source srcSet={posterImageSm.url} media={`(min-width: ${SizeBreakpointSm}px)`} />
            ) : null}
            {backgroundImageHasURL && fallbackImage && (
              <Image
                src={fallbackImage.url}
                alt={fallbackAlt}
                sizes='100vw'
                width={0}
                height={0}
                style={{
                  width: '100%',
                  height: '100%'
                }}
                quality={NEXT_IMAGE_QUALITY}
                priority
              />
            )}
          </picture>
        ) : (
          fallbackImage &&
          typeof fallbackImage === 'string' && (
            <Image
              src={fallbackImage}
              alt={fallbackAlt}
              sizes='100vw'
              width={0}
              height={0}
              style={{
                width: '100%',
                height: '100%'
              }}
              quality={NEXT_IMAGE_QUALITY}
              priority
            />
          )
        )}
      </div>
      <div className={styles['hero__copy']}>
        <Container
          className={'container container--half-padding-xs'}
          // zIndex so it overlaps absolutely positioned image
          style={{ zIndex: 2 }}
        >
          {subHeadline && <Eyebrow title={subHeadline} color='accent' size='md' isBold noMarginTop noMarginBottom />}
          <div className={styles['headline-btn-container']}>
            <div className={styles['headline-container']}>
              {title && (
                <Heading level={1} noMarginBottom noMarginTop>
                  {title}
                </Heading>
              )}
            </div>
            {buttonGroup && (
              <div className={cx([styles['btn-container'], stackButtonsOnMobile && isBelowSm && styles.stacked])}>
                {buttonGroup.map((button, index) => (
                  <Button
                    key={index}
                    {...button}
                    isFullWidth={!!isBelowSm}
                    {...(button?.anchorLink && {
                      onClick: () => handleButtonAnchor(button.anchorLink ?? '')
                    })}
                    analytics={{
                      linkModule: linkModule
                    }}
                  />
                ))}
              </div>
            )}
          </div>
          {description && (
            <div className={styles['description-container']}>
              <Paragraph text={description} fontSize={'md-plus'} readMoreText={readMoreField} />
            </div>
          )}
        </Container>
      </div>
    </div>
  )
}

export default HeroEdpHeader
